import { Menu as AntMenu } from "antd";
import { ReactNode, useMemo } from "react";

import { buildCabinetMenuItems } from "@app/shared/lib/cabinet/buildMenuItems";
import { CabinetPage } from "@app/shared/lib/cabinet/types";
import { useCabinet } from "@app/shared/lib/cabinet/CabinetProvider";
import { useLoc, useRouter, useSchool } from "@app/shared/lib/hooks";
import { DynamicIcon } from "@app/shared/ui/DynamicIcon";

type Props = {
  mode: "vertical" | "horizontal";
  onSelect?: (eky: string) => void;
};

type MenuItem = { key: string; label: ReactNode; onClick?: () => void; children?: MenuItem[] };

export const Menu: React.FC<Props> = ({ mode, onSelect }) => {
  const { navigate } = useRouter();
  const loc = useLoc();
  const school = useSchool();
  const { rootPages, childrenOf } = useCabinet();

  const items = useMemo<MenuItem[]>(() => {
    // Cabinet mode: navigate to standard routes by segment (first page → /)
    if (school.isCabinetEnabled && rootPages.length > 0) {
      const pageLabel = (page: CabinetPage): ReactNode => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {page.icon && <DynamicIcon name={page.icon} />}
          {page.name}
        </span>
      );

      return buildCabinetMenuItems<MenuItem>(
        rootPages,
        childrenOf,
        (page, path) => ({ key: path, label: pageLabel(page), onClick: () => navigate(path) }),
        (page, path, children) => {
          // Parent with children: label only expands the submenu. The page
          // itself is added as the first child item for navigation.
          const selfItem: MenuItem = { key: path, label: pageLabel(page), onClick: () => navigate(path) };
          return { key: `${path}__group`, label: pageLabel(page), children: [selfItem, ...children] };
        },
      );
    }

    // Default mode: standard navigation
    return [
      {
        key: "/",
        label: loc.word("Courses and materials"),
        onClick: () => navigate("/"),
      },
      school.isForumAvailable && {
        key: "/forum",
        label: loc.word("Forum"),
        onClick: () => navigate("/forum"),
      },
      {
        key: "/group-chat",
        label: loc.word("Group chat", { default: "Group chat" }),
        onClick: () => navigate("/group-chat"),
      },
      {
        key: "/access",
        label: loc.word("Access and payments"),
        onClick: () => navigate("/access"),
      },
      school.partnership && {
        key: "/partnership",
        label: loc.word("Partnership"),
        onClick: () => navigate("/partnership"),
      },
    ].filter(Boolean) as MenuItem[];
  }, [loc, navigate, school.isCabinetEnabled, school.isForumAvailable, school.partnership, rootPages, childrenOf]);

  return (
    <AntMenu
      selectedKeys={[window.location.pathname]}
      mode={mode}
      items={items}
      onSelect={(e) => onSelect?.(e.key)}
      className="flex-1"
    />
  );
};
